import {
  FloatingPortal,
  useMergeRefs,
} from "@floating-ui/react"
import classNames from "classnames"

import type {
  CSSProperties,
  Ref,
  ComponentPropsWithoutRef,
} from "react"
import {
  forwardRef,
  useRef,
  useMemo,
} from "react"

import styles from "./Toast.module.scss"
import { useToastContext } from "./useToastContext"
import { useToasterContext } from "./useToasterContext"
import { useToastsContext } from "./useToastsContext"

type Props = ComponentPropsWithoutRef<"div">

/*
 * thie component renders its children in a floating portal in the toaster
 */
export const ToastContent = forwardRef<HTMLDivElement, Props>(
  function ToastContent(props: Props, propRef: Ref<HTMLDivElement>) {
    const state = useToastContext()

    const { toasterRoot } = useToasterContext()
    const { status: toastStatus } = useToastsContext(state.toastId)

    const containerRef = useRef<HTMLDivElement>(null)

    const ref = useMergeRefs([
      state.refs.setFloating,
      containerRef,
      propRef,
    ])

    // fix the position of this element so that it doesn't move around during its
    // closing animation
    const containerStyle: CSSProperties | undefined = useMemo(
      () => {
        const style: CSSProperties = {
          transitionDuration: `${state.transitionMs}ms`,
        }

        const container = containerRef.current
        if (toastStatus === "close" && container) {
          const clientRect = container.getBoundingClientRect()

          style.position = "fixed"
          style.top = clientRect.top
          style.left = clientRect.left
          style.width = clientRect.width
        }

        return style
      },
      [
        containerRef,
        toastStatus,
        state.transitionMs,
      ],
    )

    const className = classNames(
      styles.toastContent,
      props.className,
    )

    return (
      <FloatingPortal root={toasterRoot}>
        {state.isMounted && (
          <div
            {...props}
            aria-describedby={state.descriptionId}
            aria-labelledby={state.labelId}
            className={className}
            data-status={toastStatus}
            ref={ref}
            style={containerStyle}
          />
        )}
      </FloatingPortal>
    )
  },
)
